import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import { FaLeaf } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionDto,
  AwardCriterionType,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
  WeightType,
} from "../../../../../autogen/bff-api";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import {
  AWARD_CRITERION_TYPE_OPTIONS,
  criterionTypeSelectValue,
  ENVIRONMENT_EXEMPTION_TYPES,
  ENVIRONMENT_GROUP_VALUE,
  isEnvironmentCriterionType,
  MIN_ENVIRONMENT_WEIGHTING_PERCENT,
} from "../utils/publicProcurementOptions";
import { exemptionTitleKey } from "../utils/environmentJustificationTemplates";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";

// t('Type') t('Name') t('Weight') t('Description') t('Delete criterion') t('No environmental weighting')

interface Props {
  eventId: string;
  index: number;
  criterion: AwardCriterionDto;
  isLocked: boolean;
  onChange: (next: AwardCriterionDto) => void;
  onDelete: (index: number) => void;
  // Present only for environment criterion cards: enters a § 5c "no environmental weighting" exemption
  // with the chosen ground (the parent owns the cross-criteria mutation + confirmation).
  onChooseExemption?: (exemptionType: AwardCriterionType) => void;
}

export const AwardCriterionCard = ({
  eventId,
  index,
  criterion,
  isLocked,
  onChange,
  onDelete,
  onChooseExemption,
}: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  const { fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const weightFieldError = fieldError(
    `eventData.awardCriteria.bestPriceQualityRatio.criteria.${index}.weight.number`,
  );

  // Local string buffer absorbs empty/non-numeric drafts; only well-formed numbers propagate
  // to the coordinator. Range checking lives on the BFF (FieldErrorCode_OutOfRange).
  const [localWeight, setLocalWeight] = useState<string>(
    criterion.weight?.number != null ? String(criterion.weight.number) : ""
  );

  useEffect(() => {
    setLocalWeight(criterion.weight?.number != null ? String(criterion.weight.number) : "");
  }, [criterion.weight?.number]);

  // The parent (AwardCriteriaStep) funnels onChange into the save-coordinator with
  // debounceMs=300, so this component propagates every keystroke and lets the
  // coordinator collapse them — a ref keeps us calling the latest parent callback.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const updateType = (value: string) => {
    if (value === ENVIRONMENT_GROUP_VALUE) {
      // Designate this row as the environment criterion. The BFF derives the sub-type (criteria vs
      // specification) from the total environment weighting, so tag it as the environment family and
      // seed the § 5b floor when there is no weight yet.
      const currentWeight = criterion.weight?.number ?? null;
      if (currentWeight == null) {
        setLocalWeight(String(MIN_ENVIRONMENT_WEIGHTING_PERCENT));
        onChangeRef.current({
          ...criterion,
          type: AwardCriterionType.QualityEnvironmentalCriteria,
          weight: { number: MIN_ENVIRONMENT_WEIGHTING_PERCENT, type: WeightType.Percentage },
        });
      } else {
        onChangeRef.current({ ...criterion, type: AwardCriterionType.QualityEnvironmentalCriteria });
      }
      return;
    }
    onChangeRef.current({ ...criterion, type: value as AwardCriterionType });
  };
  const updateName = (name: string) => onChangeRef.current({ ...criterion, name });
  const updateDescription = (description: string) => onChangeRef.current({ ...criterion, description });
  const updateWeight = (raw: string) => {
    setLocalWeight(raw);
    // Only the weight changes here — the environment sub-type (criteria vs specification) is derived by
    // the BFF from the summed environment weighting, so the client never sets it per criterion.
    if (raw === "") {
      onChangeRef.current({ ...criterion, weight: undefined });
      return;
    }
    // Weights are integer-only on this form to side-step locale display,
    // float drift, and BigDecimal precision mismatch with the BFF.
    const parsed = Number.parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(parsed)) return;
    const n = Math.round(parsed);
    onChangeRef.current({ ...criterion, weight: { number: n, type: WeightType.Percentage } });
  };

  return (
    <Box
      data-testid="award-criteria-card"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="smBorder"
      p="4"
      mb="3"
    >
      <HStack align="flex-start" spacing="4" mb="3">
        <FormControl flex="1" isDisabled={isLocked}>
          <FormLabel fontSize="xs" color="smMuted">
            {t("Type")}
          </FormLabel>
          <Select
            size="sm"
            rounded="md"
            value={criterionTypeSelectValue(criterion.type)}
            onChange={(e) => updateType(e.target.value)}
          >
            {AWARD_CRITERION_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl flex="1" isDisabled={isLocked} isInvalid={!!weightFieldError}>
          <FormLabel fontSize="xs" color="smMuted">
            {t("Weight")}
          </FormLabel>
          <InputGroup size="sm">
            <Input
              type="text"
              inputMode="decimal"
              rounded="md"
              value={localWeight}
              onChange={(e) => updateWeight(e.target.value)}
              onBlur={() =>
                setLocalWeight(
                  criterion.weight?.number != null ? String(criterion.weight.number) : ""
                )
              }
            />
            <InputRightAddon rounded="md">%</InputRightAddon>
          </InputGroup>
          {weightFieldError && (
            <FormErrorMessage fontSize="xs">{formatFieldError(weightFieldError)}</FormErrorMessage>
          )}
        </FormControl>

        <IconButton
          aria-label={t("Delete criterion") ?? "Delete"}
          icon={<DeleteIcon />}
          variant="ghost"
          size="sm"
          isDisabled={isLocked}
          onClick={() => onDelete(index)}
        />
      </HStack>

      <FormControl mb="3" isDisabled={isLocked}>
        <FormLabel fontSize="xs" color="smMuted">
          {t("Name")}
        </FormLabel>
        <Input
          size="sm"
          rounded="md"
          value={criterion.name ?? ""}
          onChange={(e) => updateName(e.target.value)}
        />
      </FormControl>

      <Box>
        <Text fontSize="xs" color="smMuted" mb="1">
          {t("Description")}
          <Text as="span" color="smWarning" ml="1">
            *
          </Text>
        </Text>
        <TipTap
          content={criterion.description ?? ""}
          onUpdate={(value: string) => updateDescription(value)}
          editable={!isLocked}
          toolbar={{ underline: false, headings: false, colors: false, link: false, table: false, image: false, template: false }}
          debounceWait={0}
        />
      </Box>

      {isEnvironmentCriterionType(criterion.type) && onChooseExemption && (
        <Flex justify="flex-end" mt="3">
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              variant="outline"
              leftIcon={<FaLeaf />}
              rightIcon={<ChevronDownIcon />}
              isDisabled={isLocked}
            >
              {t("No environmental weighting")}
            </MenuButton>
            <MenuList>
              {ENVIRONMENT_EXEMPTION_TYPES.map((type) => (
                <MenuItem key={type} onClick={() => onChooseExemption(type)}>
                  {t(exemptionTitleKey(type))}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>
      )}
    </Box>
  );
};
